import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Spinner, Alert, Badge } from 'react-bootstrap';

import ChartComSliders from 'components/Windx/ChartComSliders';
import AvisoFiltrosAlterados from 'components/Windx/AvisoFiltrosAlterados';
import AvisoConsultaInviavel from 'components/Windx/AvisoConsultaInviavel';

import { useGetPainelOptionsQuery } from 'services/api/windx';
import {
  ChartRequestSH,
  EChartsOption,
} from 'helper/windx';
import { derivarPeriodoEixoX } from 'helper/windx/derivarPeriodoEixoX';
import { derivarExtensaoRetroativa } from 'helper/windx/derivarExtensaoRetroativa';
import useChartDataMergedSH from 'helper/windx/useChartDataMergedSH';
import useEstimativaWindx from 'helper/windx/useEstimativaWindx';
import useSimulacoesAntigasAtivas from 'components/Windx/ControlPanel/SimulacoesAntigasSalvas/useSimulacoesAntigasAtivas';
import {
  selectWindxIdSubdivisaoEspacial,
  selectWindxIdsEntidades,
  selectWindxIdsModelos,
  selectWindxTipoRodada,
  selectWindxOpcoesRmv,
  selectWindxUnidade,
  selectWindxFatorRen,
  selectWindxDataSimulacao,
  selectWindxTelaAtiva,
  selectWindxGranularidadeSH,
  selectWindxTriggerConsulta,
  selectWindxConsultaPendente,
} from 'redux/reducers/windxControlPanelReducer';

/**
 * Grafico ECharts da tela Weol-SH.
 *
 * Le filtros do redux, dispara `getChartDataSH` quando todos os
 * obrigatorios estao presentes (subdivisao + >=1 entidade + >=1 modelo)
 * e renderiza o `option` que o backend ja entrega pronto (Phase A,
 * WINDX-008.4).
 *
 * Estados:
 * - skip   : faltam filtros obrigatorios -> placeholder de instrucao
 * - loading: spinner
 * - error  : Alert vermelho com mensagem do backend (422/400 do Pydantic)
 * - empty  : `total_pontos === 0` -> Alert info
 * - ok     : <ReactECharts option={data.option} />
 */
interface GraficoSHProps {
  // Espelha `isFetching` para fora sem mudar nada do render. Existe para a
  // `BarraConsulta` do mobile, que fica fixa no rodape e precisa do estado da
  // consulta — que so este componente conhece (o payload e montado aqui).
  // Desktop nao passa: o default no-op mantem o comportamento identico.
  onLoadingChange?: (carregando: boolean) => void;
  // Abre o grafico em tela cheia. So o mobile passa — vira o botao de
  // expandir no cabecalho do card do `ChartComSliders`.
  onExpandir?: () => void;
}

export default function GraficoSH({ onLoadingChange, onExpandir }: GraficoSHProps = {}) {
  const subdivisao = useSelector(selectWindxIdSubdivisaoEspacial);
  const idsEntidades = useSelector(selectWindxIdsEntidades);
  const idsModelos = useSelector(selectWindxIdsModelos);
  const tipoRodada = useSelector(selectWindxTipoRodada);
  const opcoesRmv = useSelector(selectWindxOpcoesRmv);
  const unidade = useSelector(selectWindxUnidade);
  const fatorRen = useSelector(selectWindxFatorRen);
  const dataSimulacao = useSelector(selectWindxDataSimulacao);
  const telaAtiva = useSelector(selectWindxTelaAtiva);
  const granularidadeSH = useSelector(selectWindxGranularidadeSH);
  const triggerConsulta = useSelector(selectWindxTriggerConsulta);
  const consultaPendente = useSelector(selectWindxConsultaPendente);

  // Medidor de complexidade: quando o veredito atual eh `vermelho`, a area
  // do grafico passa a exibir um aviso ao inves do grafico antigo (que pode
  // ser stale de uma consulta anterior). O painel de controle ja sinaliza o
  // mesmo problema com mais detalhe via MedidorComplexidade.
  const { estimativa: estimativaComplexidade } = useEstimativaWindx();

  // FE-WINDX-013 (2026-05-20): Fetch painelOptions para derivar o periodo
  // do grafico (dataInicio/dataFim) a partir do horizonte do modelo selecionado.
  // Usa helper derivarPeriodoEixoX() em tempo de request.
  const { data: painelOptions } = useGetPainelOptionsQuery('sh');

  const simsAtivas = useSimulacoesAntigasAtivas();

  // FE-WINDX-043 v7: filtros completos = subdivisao + entidades obrigatorios
  // SEMPRE. Modelos do Estudo Automatico OU >=1 sim antiga ativa.
  const filtrosCompartilhadosOk = (
    typeof subdivisao === 'number'
    && idsEntidades.length > 0
  );
  const estudoAutomaticoOk = (
    filtrosCompartilhadosOk
    && idsModelos.length > 0
    && opcoesRmv.length > 0
    // WINDX-056: sem default no boot, tipoRodada pode ser undefined ate o
    // usuario escolher. Mantem o placeholder enquanto nao escolhido (alinha
    // com selectWindxFiltrosCompletos do reducer).
    && !!tipoRodada
  );
  const temAlgumaSim = simsAtivas.length > 0;
  const filtrosCompletos = filtrosCompartilhadosOk && (estudoAutomaticoOk || temAlgumaSim);

  // Derivar dataInicio/dataFim do horizonte. Quando temos modelos do Estudo
  // Automatico, usa eles; senao usa os modelos da PRIMEIRA sim ativa.
  const periodo = useMemo(() => {
    if (!painelOptions) return undefined;
    const idsParaHorizonte = idsModelos.length > 0
      ? idsModelos
      : (simsAtivas[0]?.id_modelos ?? []);
    if (idsParaHorizonte.length === 0) return undefined;
    const hoje = new Date().toISOString().split('T')[0];
    const painelOptionsAdaptadas = painelOptions.modelos.map((modelo) => ({
      id: modelo.id,
      label: modelo.label,
      config: { dias_previsao: modelo.dias_previsao },
    }));
    return derivarPeriodoEixoX(hoje, idsParaHorizonte, painelOptionsAdaptadas);
  }, [painelOptions, idsModelos, simsAtivas]);

  // Filtros compartilhados (subdiv + ent + periodo + unidade) — usados tanto
  // pelo Estudo Automatico quanto pelas N chamadas das sims antigas.
  const filtrosCompartilhados = useMemo<
  Pick<ChartRequestSH, 'frequencia_temporal' | 'subdivisao_espacial' | 'id_entidades'
  | 'data_inicio' | 'data_fim' | 'unidade'> | undefined
  >(() => {
    if (!filtrosCompartilhadosOk || !periodo) return undefined;
    return {
      frequencia_temporal: granularidadeSH === 'diario' ? 'd' : 'sh',
      subdivisao_espacial: subdivisao as number,
      id_entidades: idsEntidades,
      data_inicio: periodo.dataInicio,
      data_fim: periodo.dataFim,
      unidade,
    };
  }, [filtrosCompartilhadosOk, periodo, subdivisao, idsEntidades, unidade, granularidadeSH]);

  // payloadBase do Estudo Automatico: undefined se modelos/rmv vazios
  // (sim antiga sozinha eh permitida).
  const payloadBase = useMemo<ChartRequestSH | undefined>(() => {
    // WINDX-056: `!tipoRodada` no guard estreita o tipo (TipoRodada | undefined
    // -> TipoRodada) para o `tipo_rodada` obrigatorio do ChartRequestSH. Em
    // runtime `estudoAutomaticoOk` ja exige `!!tipoRodada`.
    if (!estudoAutomaticoOk || !filtrosCompartilhados || !tipoRodada) return undefined;
    return {
      ...filtrosCompartilhados,
      id_modelos: idsModelos,
      tipo_rodada: tipoRodada,
      opcoes_rmv: opcoesRmv,
      // Padronizacao REN (2026-05-28): overlay opt-in da serie REN. BE anexa
      // a serie para a subdivisao espacial corrente (todas suportadas).
      incluir_fator_ren: fatorRen,
    };
  }, [estudoAutomaticoOk, filtrosCompartilhados, idsModelos, tipoRodada, opcoesRmv, fatorRen]);

  const {
    data, isFetching, isError, error,
  } = useChartDataMergedSH({
    payloadBase,
    telaAtiva,
    triggerConsulta,
    simsAtivas,
    filtrosCompartilhados,
  });

  // Acima dos early-returns de proposito: hooks nao podem viver abaixo deles.
  useEffect(() => {
    onLoadingChange?.(isFetching);
  }, [isFetching, onLoadingChange]);

  if (!filtrosCompletos) {
    return (
      <div className="mt-4 p-4 border rounded text-muted text-center">
        Selecione subdivisão, ao menos uma entidade e, no Estudo Automático, um
        modelo com Tipo de Rodada e RMV — ou marque uma Simulação Antiga — para
        gerar o gráfico.
      </div>
    );
  }

  if (estimativaComplexidade?.veredito === 'vermelho') {
    return <AvisoConsultaInviavel />;
  }

  if (consultaPendente) {
    return <AvisoFiltrosAlterados />;
  }

  if (isFetching) {
    return (
      <div className="mt-4 p-5 text-center" data-testid="windx-grafico-sh-loading">
        <Spinner animation="border" role="status" />
        <div className="mt-2 text-muted">Carregando gráfico…</div>
      </div>
    );
  }

  if (isError) {
    const apiMessage = (error as { data?: { description?: string; message?: string } } | undefined)
      ?.data?.description
      || (error as { data?: { description?: string; message?: string } } | undefined)?.data?.message
      || 'Falha ao carregar dados do gráfico.';
    return (
      <Alert variant="danger" className="mt-4" data-testid="windx-grafico-sh-error">
        {apiMessage}
      </Alert>
    );
  }

  if (data && data.metadata.total_pontos === 0) {
    return (
      <Alert variant="info" className="mt-4" data-testid="windx-grafico-sh-empty">
        Nenhum dado disponível para os filtros selecionados.
      </Alert>
    );
  }

  if (!data) return null;

  // WINDX-036 (ADR-028, contrato v7.1): processa series com extensao
  // retroativa — aplica opacity 0.7 nas series com defasagem >= 2 dias e
  // monta badges para o aviso visual escalonado.
  // dataRodada referencia: usa `data_simulacao` quando informado; caso
  // contrario hoje (alinha com CURRENT_DATE do BE).
  const dataRodadaRef = dataSimulacao || new Date().toISOString().slice(0, 10);
  const { option, badges } = derivarExtensaoRetroativa(
    data.option as EChartsOption,
    dataRodadaRef,
  );

  return (
    <>
      {badges.length > 0 && (
        <div
          className="mt-2 d-flex flex-wrap gap-2"
          data-testid="windx-grafico-sh-extensao-badges"
        >
          {badges.map((b) => (
            <Badge
              key={`${b.nome}|${b.dataExtensao}`}
              bg={b.severidade === 'aviso' ? 'warning' : 'secondary'}
              text={b.severidade === 'aviso' ? 'dark' : undefined}
              title={`Modelo de extensao em fallback retroativo: usando rodada de ${b.dataExtensaoFmt} (defasagem ${b.defasagemDias}d).`}
            >
              {`${b.nome} ext. de ${b.dataExtensaoFmt} (D-${b.defasagemDias})`}
            </Badge>
          ))}
        </div>
      )}
      {/* WINDX-040 Frente B (revisao 2026-05-25): mantem height:32rem como base
        (igual ao comportamento original) + flex-grow-1 para crescer quando
        pai for flex container. ChartComSliders usa h-100 que precisa de
        pai com height definido — minHeight nao satisfaz isso. */}
      <div
        className="mt-4 position-relative w-100 flex-grow-1"
        style={{ height: '32rem' }}
        data-testid="windx-grafico-sh-chart"
      >
        <ChartComSliders
          className="h-100"
          option={option}
          kicker={granularidadeSH === 'diario' ? 'WEOL-SH · Diário' : 'WEOL-SH · Semi-horário'}
          onExpandir={onExpandir}
        />
      </div>
    </>
  );
}
